import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { AttachmentMutation, putMxAction } from '../../services/apiNew';
import { addToast, ToastCategories, ToastTypes } from '../../models/toasts';

type MxActionUpdatePatch = {
  title?: string;
  description?: string;
  attachments?: AttachmentMutation[];
};

function usePersistMxActionUpdate(
  mxItemId: string,
  actionSingular: string,
): (actionId: string, patch: MxActionUpdatePatch) => Promise<void> {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();

  return useCallback(
    async (actionId: string, patch: MxActionUpdatePatch) => {
      try {
        await putMxAction(actionId, patch);
        queryClient.invalidateQueries({ queryKey: ['mxActions', mxItemId] });
      } catch (err) {
        const errorData =
          typeof axios.isAxiosError === 'function' && axios.isAxiosError(err)
            ? err.response?.data
            : (err as { response?: { data?: { message?: unknown } } })?.response?.data;
        const message =
          errorData?.message != null
            ? String(errorData.message)
            : formatMessage({ id: 'text.unableToUpdateAction' }, { actionSingular });
        dispatch(
          addToast({
            payload: {
              title: formatMessage({ id: 'text.actionUpdateError' }, { actionSingular }),
              message,
              type: ToastTypes.ERROR,
              category: ToastCategories.FLAG,
            },
          }),
        );
        throw err;
      }
    },
    [dispatch, formatMessage, mxItemId, queryClient, actionSingular],
  );
}

export default usePersistMxActionUpdate;
